import dayjs, { Dayjs } from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';

dayjs.extend(isoWeek);

/** Current date as week anchor. ISO week (Mon–Sun) makes Sunday anchors safe. */
export const getDefaultWeek = (): Dayjs => dayjs();

/** Labels for Mon–Sun of the ISO week containing `date` (e.g. "Mon Mar 10"). */
export const getIsoWeekDayLabels = (date: Dayjs): string[] =>
  Array.from({ length: 7 }, (_, index) => date.startOf('isoWeek').add(index, 'day').format('ddd MMM D'));

/** Dayjs for each Mon–Sun column of the ISO week containing `date`, keyed by label. */
export const getIsoWeekDayDates = (date: Dayjs): Record<string, Dayjs> => {
  const map: Record<string, Dayjs> = {};
  Array.from({ length: 7 }, (_, index) => {
    const d = date.startOf('isoWeek').add(index, 'day');
    map[d.format('ddd MMM D')] = d;
  });
  return map;
};

export const toApiDateRange = (rangeStart: Dayjs, rangeEnd: Dayjs) => ({
  start: rangeStart.startOf('day').toISOString(),
  end: rangeEnd.endOf('day').toISOString(),
});

export const getIsoWeekRange = (date: Dayjs): { start: Dayjs; end: Dayjs } => ({
  start: date.startOf('isoWeek'),
  end: date.endOf('isoWeek'),
});

export const getIsoWeekApiDateRange = (date: Dayjs) => {
  const { start, end } = getIsoWeekRange(date);
  return toApiDateRange(start, end);
};

export const getSeasonStartForYear = (annualAnchor: Dayjs, year: number): Dayjs => {
  const anchorInYear = dayjs().year(year).month(annualAnchor.month()).date(annualAnchor.date());

  return anchorInYear.startOf('isoWeek');
};

export const getSeasonYearForSeasonStart = (annualAnchor: Dayjs, seasonStart: Dayjs): number => {
  for (let year = seasonStart.year() - 1; year <= seasonStart.year() + 1; year += 1) {
    if (getSeasonStartForYear(annualAnchor, year).isSame(seasonStart, 'day')) {
      return year;
    }
  }

  return seasonStart.year();
};

export const resolveSeasonRange = (
  annualAnchor: Dayjs,
  seasonStart: Dayjs,
  seasonYear = getSeasonYearForSeasonStart(annualAnchor, seasonStart)
): { start: Dayjs; end: Dayjs } => {
  const start = seasonStart.startOf('day');
  const nextSeasonStart = getSeasonStartForYear(annualAnchor, seasonYear + 1);
  const end = nextSeasonStart.subtract(1, 'day');

  return { start, end };
};

export const computeCurrentSeasonStart = (annualAnchor: Dayjs): Dayjs => {
  const currentYear = dayjs().year();
  const anchorThisYear = dayjs().year(currentYear).month(annualAnchor.month()).date(annualAnchor.date());

  if (dayjs().isBefore(anchorThisYear, 'day')) {
    return getSeasonStartForYear(annualAnchor, currentYear - 1);
  }

  return getSeasonStartForYear(annualAnchor, currentYear);
};

export interface StartDatePreview {
  season: string;
  week1Begins: string;
  period1: string;
  seasonEnd: string;
}

export const computeStartDatePreview = (anchor: Dayjs): StartDatePreview => {
  const seasonYear = anchor.year();
  const seasonStart = getSeasonStartForYear(anchor, seasonYear);
  const { end: seasonEnd } = resolveSeasonRange(anchor, seasonStart, seasonYear);
  const period1End = seasonStart.add(3, 'week');

  return {
    season: `${seasonYear}–${seasonYear + 1}`,
    week1Begins: seasonStart.format('ddd, MMM D, YYYY'),
    period1: `${seasonStart.format('MMM D')} – ${period1End.format('MMM D')}`,
    seasonEnd: seasonEnd.format('MMM D, YYYY'),
  };
};

export const getDefaultAnnualAnchor = (): Dayjs => dayjs().startOf('year');

export const getAnnualAnchorInCurrentYear = (anchor: Dayjs): Dayjs => anchor.year(dayjs().year());

export const formatAnnualAnchorDisplayDate = (anchor: Dayjs): string =>
  getAnnualAnchorInCurrentYear(anchor).format('MMM D, YYYY');

export const resolveSeasonFromAnchor = (anchor: Dayjs) => ({
  annualAnchor: anchor,
  seasonStart: computeCurrentSeasonStart(anchor),
});
